/**
 * Patient Page Component
 *
 * Displays a detailed view of a specific patient and their associated meetings.
 * Provides functionality to view, add, edit, and delete meetings based on user permissions.
 *
 * Features:
 * - Comprehensive meeting management interface
 * - Role-based access control for meeting operations
 * - Confirmation dialogs for destructive actions
 * - Responsive layout with Material-UI components
 */

import React, { useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";

import {
  Stack,
  Typography,
  useTheme,
  IconButton,
  TextField,
  Button,
  InputAdornment,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";

import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

// Store hooks for data fetching and mutations
import {
  useCancelMeeting,
  useGetPatient,
  useListOrganisations,
  useListUsers,
  useGetMeetingsWithIds,
} from "../../store";

// Type definitions and utility functions
import { ActiveStatus, Meeting, MeetingStatus } from "../../types";

// UI Components
import {
  ScheduleMeetingModal,
  MeetingInfoModal,
} from "../../components/meeting-management";
import CancelMeetingConfirmationModal from "./components/cancel-meeting-confirmation-modal";
import MainLayout from "../../layouts/main-layout";
import PatientMeetingsTable from "./components/patient-meetings-table";
import PatientChip from "../../components/chips/patient-chip";
import EditablePatientStatusChip from "../patients/components/editable-patient-status-chip";
import {
  createTeamDictionary,
  createUserDictionary,
} from "../../utils/create-dictionary";

const PatientPage: React.FC = () => {
  // Extract patient ID from URL parameters
  const [searchParams] = useSearchParams();
  const patientId = searchParams.get("patientId") || "";
  const navigate = useNavigate();

  // Navigation handler
  const handleBackClick = () => {
    navigate("/patients");
  };

  // Filter state management
  const [searchTerm, setSearchTerm] = React.useState("");
  const [startDate, setStartDate] = React.useState<Date | null>(null);
  const [endDate, setEndDate] = React.useState<Date | null>(null);
  const [sortBy, setSortBy] = React.useState<string>("startTime");
  const [sortOrder, setSortOrder] = React.useState<"asc" | "desc">("desc");

  // Modal state management
  const [selectedMeeting, setSelectedMeeting] = React.useState<Meeting | null>(
    null,
  );
  const [addModalOpen, setAddModalOpen] = React.useState(false);
  const [editModalOpen, setEditModalOpen] = React.useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = React.useState(false);

  const { data: patient, isLoading: patientLoading } = useGetPatient(patientId);
  const { data: patientMeetings, isLoading: meetingLoading } =
    useGetMeetingsWithIds(patient?.meetingIds || []);
  const { data: teams = [], isLoading: teamsLoading } = useListOrganisations();
  const { data: users = [], isLoading: usersLoading } = useListUsers();
  const { mutate: cancelMeeting } = useCancelMeeting();

  const unfilteredMeetingsCount = patientMeetings?.length || 0;

  const meetings = useMemo(() => {
    return (patientMeetings || [])
      ?.filter((item) => item != null)
      .filter((item) => item.meetingType != MeetingStatus.Proposed);
  }, [patientMeetings]);

  const teamDictionary = useMemo(() => {
    return createTeamDictionary(teams);
  }, [teams]);

  const userDictionary = useMemo(() => {
    return createUserDictionary(users);
  }, [users]);

  // Computed state
  const isLoading =
    patientLoading || meetingLoading || teamsLoading || usersLoading;

  // Modal handlers for adding meetings
  const openAdd = () => {
    setAddModalOpen(true);
  };

  const closeAdd = () => {
    setAddModalOpen(false);
  };

  // Modal handlers for editing meetings
  const openEdit = (meeting: Meeting) => {
    setSelectedMeeting(meeting);
    setEditModalOpen(true);
  };

  const closeEdit = () => {
    setEditModalOpen(false);
  };

  // Cancel confirmation handlers
  const deleteButtonClick = (meeting: Meeting) => {
    setSelectedMeeting(meeting);
    setCancelConfirmOpen(true);
  };

  const confirmCancel = () => {
    if (selectedMeeting) {
      cancelMeeting(selectedMeeting);
    }
    setCancelConfirmOpen(false);
  };

  const cancelCancel = () => {
    setCancelConfirmOpen(false);
  };

  // Helper functions
  const getUserName = (userId: string) => {
    const user = users.find((u) => u.userId === userId);
    return user?.fullName || "Unknown User";
  };

  const getTeamName = (teamId: string) => {
    const team = teams.find((team) => team.organisationId === teamId);
    return team?.name || "Unknown Team";
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  // Filter and sort meetings
  let filteredMeetings = meetings.filter((meeting) => {
    if (!searchTerm) return true;

    const searchTermLower = searchTerm.toLowerCase();
    const attendeeMatch =
      meeting.attendeeIds?.some((attendeeId) =>
        getUserName(attendeeId).toLowerCase().includes(searchTermLower),
      ) || false;
    const teamMatch =
      meeting.organisationIds?.some((teamId) =>
        getTeamName(teamId).toLowerCase().includes(searchTermLower),
      ) || false;

    return (
      getUserName(meeting.creatorId).toLowerCase().includes(searchTermLower) ||
      teamMatch ||
      attendeeMatch
    );
  });

  if (startDate) {
    filteredMeetings = filteredMeetings.filter((meeting) => {
      const meetingDate = new Date(meeting.startTime);
      return meetingDate >= startDate;
    });
  }

  if (endDate) {
    filteredMeetings = filteredMeetings.filter((meeting) => {
      const meetingDate = new Date(meeting.startTime);
      return meetingDate <= endDate;
    });
  }

  filteredMeetings = [...filteredMeetings].sort((a, b) => {
    let aValue: any = a[sortBy as keyof Meeting];
    let bValue: any = b[sortBy as keyof Meeting];

    if (sortBy === "startTime" || sortBy === "endTime") {
      if (!aValue && !bValue) return 0;
      if (!aValue) return 1;
      if (!bValue) return -1;
      aValue = new Date(aValue);
      bValue = new Date(bValue);
    } else if (sortBy === "creatorId") {
      aValue = getUserName(aValue).toLowerCase();
      bValue = getUserName(bValue).toLowerCase();
    } else if (typeof aValue === "string") {
      aValue = aValue.toLowerCase();
      bValue = bValue?.toLowerCase() || "";
    }

    if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
    if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
    return 0;
  });

  // Theme for consistent styling
  const theme = useTheme();
  const normalTextColor = theme.palette.text.primary;
  const isPatientInactive = patient?.status === ActiveStatus.Inactive;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <MainLayout fixedHeight>
        <Stack
          sx={{
            flex: 1,
            minHeight: 0,
          }}
        >
          {/* Page header with patient information */}

          <Stack
            direction={"row"}
            justifyContent="space-between"
            alignItems="center"
            sx={{ flexShrink: 0 }}
            mb={2}
          >
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography
                variant="h3"
                color={normalTextColor}
                onClick={handleBackClick}
                sx={{
                  cursor: "pointer",
                  "&:hover": {
                    opacity: 0.7,
                    textDecoration: "underline",
                  },
                }}
              >
                Patients
              </Typography>
              <ChevronRightIcon
                sx={{ color: normalTextColor, fontSize: "1.5rem" }}
              />
              {patient && patient.displayId && (
                <PatientChip label={patient.displayId} size="large" />
              )}
            </Stack>
            {patient && isPatientInactive ? (
              <EditablePatientStatusChip patient={patient} size="large" />
            ) : (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={openAdd}
                sx={{
                  borderRadius: "20px",
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Schedule Meeting
              </Button>
            )}
          </Stack>

          {/* Tab-based layout */}
          <Stack sx={{ width: "100%", flex: 1, minHeight: 0 }}>
            <Stack sx={{ flex: 1, minHeight: 0 }}>
              {/* Filters */}
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                sx={{ flexShrink: 0 }}
              >
                <TextField
                  placeholder="Search meetings by team, attendee, or creator..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  size="small"
                  sx={{
                    flexGrow: 1,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "0.5rem",
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  slotProps={{ textField: { size: "small" } }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "0.5rem",
                    },
                  }}
                />
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  slotProps={{ textField: { size: "small" } }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "0.5rem",
                    },
                  }}
                />
                <IconButton
                  title="Clear Filters"
                  onClick={() => {
                    setSearchTerm("");
                    setStartDate(null);
                    setEndDate(null);
                  }}
                  size="small"
                  sx={{ p: 1 }}
                >
                  <ClearIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Stack>

              {/* Table */}
              <Stack sx={{ flex: 1, minHeight: 0, pb: 1 }}>
                {!isLoading && filteredMeetings.length === 0 ? (
                  <Stack
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: 200,
                      flexDirection: "column",
                    }}
                  >
                    <Typography variant="h6" color="text.secondary">
                      No meetings found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {searchTerm || startDate || endDate
                        ? "Try adjusting your search or date filters"
                        : "Schedule a meeting to get started"}
                    </Typography>
                  </Stack>
                ) : (
                  <PatientMeetingsTable
                    filteredMeetings={filteredMeetings}
                    unfilteredMeetingsCount={unfilteredMeetingsCount}
                    isLoading={isLoading}
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                    userDictionary={userDictionary}
                    teamDictionary={teamDictionary}
                    openEdit={openEdit}
                    deleteButtonClick={deleteButtonClick}
                  />
                )}
              </Stack>
            </Stack>
          </Stack>

          {/* Cancel confirmation modal */}
          <CancelMeetingConfirmationModal
            open={cancelConfirmOpen}
            meeting={selectedMeeting}
            onConfirm={confirmCancel}
            onClose={cancelCancel}
          />

          {/* Meeting details/edit modal */}
          {selectedMeeting && (
            <MeetingInfoModal
              open={editModalOpen}
              handleClose={closeEdit}
              meetingId={selectedMeeting.meetingId}
            />
          )}

          {/* Add new meeting modal */}
          <ScheduleMeetingModal
            open={addModalOpen}
            handleClose={closeAdd}
            patientId={patientId}
          />
        </Stack>
      </MainLayout>
    </LocalizationProvider>
  );
};

export default PatientPage;
